import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const getMe = () => api.get('/auth/me');
export const register = (data) => api.post('/auth/register', data);

// Municipalities & Barangays
export const getMunicipalities = () => api.get('/municipalities');
export const getMunicipality = (id) => api.get(`/municipalities/${id}`);
export const getBarangays = (municipalityId) => api.get('/barangays', { params: { municipality_id: municipalityId } });
export const getBarangay = (id) => api.get(`/barangays/${id}`);

// Households
export const getHouseholds = (barangayId) => api.get('/households', { params: { barangay_id: barangayId } });
export const createHousehold = (data) => api.post('/households', data);
export const batchGenerateQR = (count, barangayId) => api.post(`/households/batch-generate?count=${count}&barangay_id=${barangayId}`);
export const assignQR = (householdId, data) => api.put(`/households/${householdId}/assign`, data);
export const reissueQR = (householdId) => api.post(`/households/${householdId}/reissue`);

// Resident Portal (Public)
export const getResidentPortal = (qrCode) => axios.get(`${API_URL}/api/r/${qrCode}`);

// OTP
export const requestOTP = (mobile, householdId) => axios.post(`${API_URL}/api/otp/request`, { mobile, household_id: householdId });
export const verifyOTP = (mobile, otpCode, householdId) => axios.post(`${API_URL}/api/otp/verify`, { mobile, otp_code: otpCode, household_id: householdId });

// Patient Services
export const getPatientServices = (params) => api.get('/patient-services', { params });
export const createPatientService = (data) => api.post('/patient-services', data);

// Medicines
export const getMedicines = (params) => api.get('/medicines', { params });
export const createMedicine = (data) => api.post('/medicines', data);
export const stockIn = (medicineId, data) => api.post(`/medicines/${medicineId}/stock-in`, data);

// Incidents
export const getIncidents = (params) => api.get('/incidents', { params });
export const createIncident = (data) => axios.post(`${API_URL}/api/incidents`, data);
export const updateIncident = (id, data) => api.put(`/incidents/${id}`, data);

// Tasks
export const getTasks = (params) => api.get('/tasks', { params });
export const createTask = (data) => api.post('/tasks', data);
export const updateTask = (id, data) => api.put(`/tasks/${id}`, data);

// Vaccinations
export const getVaccinations = (params) => api.get('/vaccinations', { params });
export const createVaccination = (data) => api.post('/vaccinations', data);

// Evacuation
export const getEvacuationCenters = (params) => api.get('/evacuation-centers', { params });
export const createEvacuationCenter = (data) => api.post('/evacuation-centers', data);
export const registerEvacuee = (data) => api.post('/evacuees', data);

// Requests
export const getRequests = (params) => api.get('/requests', { params });
export const createRequest = (data) => axios.post(`${API_URL}/api/requests`, data);
export const updateRequest = (id, data) => api.put(`/requests/${id}`, data);

// Announcements
export const getAnnouncements = (params) => api.get('/announcements', { params });
export const createAnnouncement = (data) => api.post('/announcements', data);

// Audit Logs
export const getAuditLogs = (params) => api.get('/audit-logs', { params });

// Disease Alerts
export const getDiseaseAlerts = (params) => api.get('/disease-alerts', { params });
export const acknowledgeAlert = (id) => api.put(`/disease-alerts/${id}/acknowledge`);

// Emergency Alert Acknowledgment (for Critical Alert Mode)
export const acknowledgeEmergencyAlert = async (alertId, alertType = 'emergency') => {
  try {
    if (alertType === 'disease') {
      return api.put(`/disease-alerts/${alertId}/acknowledge`);
    } else if (alertType === 'incident') {
      return api.put(`/incidents/${alertId}`, { status: 'acknowledged' });
    } else {
      // Generic emergency acknowledgment
      return api.post('/audit/acknowledge', { alert_id: alertId, alert_type: alertType });
    }
  } catch (err) {
    console.error('Failed to acknowledge alert:', err);
    throw err;
  }
};

// Dashboard
export const getMunicipalDashboard = () => api.get('/dashboard/municipal');
export const getBarangayDashboard = (barangayId) => api.get('/dashboard/barangay', { params: { barangay_id: barangayId } });
export const getMedicineForecast = (barangayId) => api.get('/dashboard/medicine-forecast', { params: { barangay_id: barangayId } });

// Users
export const getUsers = (params) => api.get('/users', { params });

// Debug (dev only)
export const getOTPSessions = () => api.get('/debug/otp-sessions');

// Logo Management (public GET, admin-only POST/DELETE)
export const getLogo = () => api.get('/settings/logo');
export const uploadLogo = (data) => api.post('/settings/logo', data);
export const deleteLogo = () => api.delete('/settings/logo');

export default api;
